import { PassengerI } from '@msc-b2c/interfaces/interfaces';
import { Image, SkeletonLoader, Text } from '@msc-b2c/shared-components';
import { BookingStatusEnum, ItemTypeEnum, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import {
  breakpoints,
  formatPrice,
  getActBaseImagePath,
  getActiveSession,
  getActPlaceholderImagePath,
  getAvailablePrice,
  getFormatPriceCurrencyOrder,
  getObsBaseImagePath,
  getObsPlaceholderImagePath,
  managePlurals,
  replacePlaceholders,
  RootState,
  roundPrice,
  useAssetAltText,
  useGetBookingQuery,
} from '@msc-b2c/shared-utils';
import { skipToken } from '@reduxjs/toolkit/query';
import { getNamesOfAlreadyBookedPassengers } from '../../utils/PostBookingResultsUtilities';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  AddToCartOverviewPriceMapRow,
  AddToCartOverviewProps,
  BookedPassengerRefundTotals,
  BookedPrepaidRefundLine,
  PriceBreakdownEntry,
  RefundPricingTotalsAccumulator,
  ShopDrawerPassengerPrice,
  TotalPrice,
} from '@msc-b2c/interfaces/types';
import { getPriceByTypeAndCode } from '../../../utils/myAreaCartUtils';
import { getCabinIdOfTheLoggedUser } from '../../../utils/myAreaUtils';
import { getOrderedCabinsForPassengerDrawer } from '../../../utils/stateroomGenericUtils';
import './addtocartoverview.scss';

const accumulateBookedRefundPrice = (
  result: Map<string, BookedPassengerRefundTotals>,
  passengerId: string,
  afterDiscount?: string,
  beforeDiscount?: string
) => {
  const itemAfter = Number.parseFloat(afterDiscount ?? '0');
  const itemBefore = Number.parseFloat(beforeDiscount ?? afterDiscount ?? '0');
  const existing = result.get(passengerId);
  const currentAfter = Number.parseFloat(existing?.afterDiscount ?? '0');
  const currentBefore = Number.parseFloat(existing?.beforeDiscount ?? '0');
  result.set(passengerId, {
    afterDiscount: (currentAfter + (Number.isNaN(itemAfter) ? 0 : itemAfter)).toString(),
    beforeDiscount: (currentBefore + (Number.isNaN(itemBefore) ? 0 : itemBefore)).toString(),
  });
};

const addPerPassengerBookedRefundLines = (
  result: Map<string, BookedPassengerRefundTotals>,
  lines: BookedPrepaidRefundLine[],
  cardCodes: Set<string>,
  selectedPassengerIdSet: Set<string>
) => {
  lines
    .filter(
      (item) =>
        cardCodes.has(item.code) &&
        item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
        (item.priceBasis === PriceBasisEnum.PER_PERSON || item.priceBasis == null) &&
        item.passenger?.id &&
        selectedPassengerIdSet.has(item.passenger.id)
    )
    .forEach((item) => {
      const passengerId = item.passenger?.id;
      if (!passengerId) {
        return;
      }
      accumulateBookedRefundPrice(
        result,
        passengerId,
        item.price?.total?.afterDiscount,
        item.price?.total?.beforeDiscount
      );
    });
};

const addPerCabinBookedRefundLines = (
  result: Map<string, BookedPassengerRefundTotals>,
  lines: BookedPrepaidRefundLine[],
  cardCodes: Set<string>,
  selectedPassengerIdSet: Set<string>,
  passengersPerCabin: Record<string, PassengerI[]>
) => {
  lines
    .filter(
      (item) =>
        cardCodes.has(item.code) &&
        item.bookingStatus?.code === BookingStatusEnum.BOOKED &&
        item.priceBasis === PriceBasisEnum.PER_CABIN &&
        Boolean(item.cabin?.id)
    )
    .forEach((item) => {
      const cabinId = item.cabin?.id;
      if (!cabinId) {
        return;
      }
      const cabinPax = passengersPerCabin[cabinId] ?? [];
      const selectedInCabin = cabinPax
        .map((p) => p.id)
        .filter((id): id is string => Boolean(id) && selectedPassengerIdSet.has(id));
      if (!selectedInCabin.length) {
        return;
      }
      const after = item.price?.total?.afterDiscount;
      const before = item.price?.total?.beforeDiscount ?? after;
      const count = selectedInCabin.length;
      const shareAfter = (Number.parseFloat(after ?? '0') / count).toString();
      const shareBefore = (Number.parseFloat(before ?? '0') / count).toString();
      selectedInCabin.forEach((id) => {
        accumulateBookedRefundPrice(result, id, shareAfter, shareBefore);
      });
    });
};

const mergeBookedRefundPassengerIntoPriceMap = (
  priceMap: Map<string, AddToCartOverviewPriceMapRow>,
  passengerId: string,
  bookedPassengerTotalsById: Map<string, BookedPassengerRefundTotals>
) => {
  const bookedTotals = bookedPassengerTotalsById.get(passengerId);
  const afterValue = bookedTotals?.afterDiscount;
  if (!afterValue) {
    return;
  }
  const formattedDiscounted = getFormatPriceCurrencyOrder(formatPrice(roundPrice(afterValue)));
  if (!formattedDiscounted) {
    return;
  }
  const beforeValue = bookedTotals?.beforeDiscount;
  const formattedOriginal =
    beforeValue && Number.parseFloat(beforeValue) > Number.parseFloat(afterValue)
      ? getFormatPriceCurrencyOrder(formatPrice(roundPrice(beforeValue)))
      : undefined;
  const key = `${formattedDiscounted}|${formattedOriginal ?? ''}`;
  const existing = priceMap.get(key);
  if (existing) {
    existing.count += 1;
  } else {
    priceMap.set(key, {
      count: 1,
      discounted: formattedDiscounted,
      original: formattedOriginal,
    });
  }
};

const findShopPassengerPriceRow = (
  passengerPrices: ShopDrawerPassengerPrice[],
  passengerId: string,
  isLayerExcursionOpen: boolean,
  cardCodes: Set<string>,
  obsCodeForPricing: string | undefined
): ShopDrawerPassengerPrice | undefined =>
  passengerPrices.find((passenger) => {
    if (passenger.id !== passengerId || !passenger.isApplicable) {
      return false;
    }
    if (isLayerExcursionOpen) {
      return true;
    }

    const relatedCode = passenger.relatedItemCode;
    if (relatedCode) {
      return cardCodes.has(relatedCode);
    }

    return Boolean(obsCodeForPricing) && cardCodes.has(obsCodeForPricing as string);
  });

const mergeShopPassengerIntoPriceMap = (
  priceMap: Map<string, AddToCartOverviewPriceMapRow>,
  passengerPrice: ShopDrawerPassengerPrice | undefined
) => {
  if (!passengerPrice) {
    return;
  }
  const availableValue = getAvailablePrice(passengerPrice.total);
  if (!availableValue) {
    return;
  }
  const formattedDiscounted = getFormatPriceCurrencyOrder(formatPrice(roundPrice(availableValue)));

  if (!formattedDiscounted) {
    return;
  }
  const beforePromo = passengerPrice.total?.beforePromotionAndDiscount;

  let formattedOriginal: string | undefined;

  if (beforePromo) {
    const beforeNumeric = Number.parseFloat(beforePromo);
    const afterNumeric = Number.parseFloat(availableValue);

    if (!Number.isNaN(beforeNumeric) && beforeNumeric > afterNumeric) {
      formattedOriginal = getFormatPriceCurrencyOrder(formatPrice(roundPrice(beforePromo)));
    }
  }

  const key = `${formattedDiscounted}|${formattedOriginal ?? ''}`;
  const existing = priceMap.get(key);

  if (existing) {
    existing.count += 1;
  } else {
    priceMap.set(key, {
      count: 1,
      discounted: formattedDiscounted,
      original: formattedOriginal,
    });
  }
};

const accumulateTotalsFromBookedRefundPassenger = (
  passengerId: string,
  bookedPassengerTotalsById: Map<string, BookedPassengerRefundTotals>,
  accum: RefundPricingTotalsAccumulator
) => {
  const bookedTotals = bookedPassengerTotalsById.get(passengerId);
  const afterValue = bookedTotals?.afterDiscount;
  if (!afterValue) {
    return;
  }
  const afterNumeric = Number.parseFloat(afterValue);
  if (!Number.isNaN(afterNumeric)) {
    accum.totalAfterDiscount += afterNumeric;
  }
  const beforeValue = bookedTotals?.beforeDiscount;
  const beforeNumeric = beforeValue ? Number.parseFloat(beforeValue) : Number.NaN;
  if (!Number.isNaN(beforeNumeric) && beforeNumeric > afterNumeric) {
    accum.totalBeforePromo += beforeNumeric;
  } else if (!Number.isNaN(afterNumeric)) {
    accum.totalBeforePromo += afterNumeric;
  }
};

const accumulateTotalsFromShopPassenger = (
  passengerPrice: ShopDrawerPassengerPrice | undefined,
  accum: RefundPricingTotalsAccumulator
) => {
  if (!passengerPrice) {
    return;
  }
  const availableValue = getAvailablePrice(passengerPrice.total);
  if (!availableValue) {
    return;
  }
  const afterNumeric = Number.parseFloat(availableValue);
  if (!Number.isNaN(afterNumeric)) {
    accum.totalAfterDiscount += afterNumeric;
  }

  const beforePromo =
    passengerPrice.total?.beforePromotionAndDiscount ?? passengerPrice.total?.beforeDiscount;
  const beforeNumeric = beforePromo ? Number.parseFloat(beforePromo) : Number.NaN;

  if (!Number.isNaN(beforeNumeric) && beforeNumeric > afterNumeric) {
    accum.totalBeforePromo += beforeNumeric;
  } else if (!Number.isNaN(afterNumeric)) {
    accum.totalBeforePromo += afterNumeric;
  }
};

const getPassengersContent = ({
  passengersName,
  isMobile,
  shouldShowAllCabinLabel,
  allCabinLabel,
}: {
  passengersName: string[];
  isMobile: boolean;
  shouldShowAllCabinLabel: boolean;
  allCabinLabel?: string;
}) => {
  if (shouldShowAllCabinLabel && allCabinLabel) {
    return (
      <Text
        text={allCabinLabel}
        styleOption="add-to-cart-overview__body__passengers__names"
      />
    );
  }

  if (isMobile) {
    return passengersName.map((name) => (
      <Text
        key={name}
        text={name}
        styleOption="add-to-cart-overview__body__passengers__name"
      />
    ));
  }

  return (
    <Text
      text={passengersName.join(', ')}
      styleOption="add-to-cart-overview__body__passengers__names"
    />
  );
};

const renderPriceBreakdownRows = (priceBreakdown: PriceBreakdownEntry[], isMobile: boolean) => {
  if (!priceBreakdown.length) {
    return null;
  }

  const recordStyle = isMobile
    ? 'add-to-cart-overview__body__price-breakdown__record'
    : 'add-to-cart-overview__body__price-breakdown__records';

  // Keep every tier on one comma-separated line, but render each tier's strikethrough inline right
  // after its own price (e.g. promo on the adult line) instead of appending it after the whole label.
  // `<del>` survives sanitisation (unlike a class/style span) and renders line-through by default.
  const combinedText = priceBreakdown
    .map(({ text, originalPrice }) =>
      originalPrice ? `${text} <del>${originalPrice}</del>` : text
    )
    .join(', ');

  return (
    <div className="add-to-cart-overview__body__price-breakdown__record-row">
      <Text
        text={combinedText}
        styleOption={recordStyle}
      />
    </div>
  );
};

const getPriceBreakdownContent = ({
  isPriceLoading,
  shouldShowAllCabinLabel,
  priceBreakdown,
  isMobile,
}: {
  isPriceLoading: boolean;
  shouldShowAllCabinLabel: boolean;
  priceBreakdown: PriceBreakdownEntry[];
  isMobile: boolean;
}) => {
  if (isPriceLoading) {
    return (
      <>
        <SkeletonLoader
          width="120px"
          height="14px"
          styleOption="cmp-skeleton__loader"
        />
        <SkeletonLoader
          width="140px"
          height="14px"
          styleOption="cmp-skeleton__loader"
        />
      </>
    );
  }

  if (shouldShowAllCabinLabel) {
    return null;
  }

  return renderPriceBreakdownRows(priceBreakdown, isMobile);
};

function AddToCartOverview({
  postBookingCard,
  passengersToAdd,
  useBookedRefundPricing = false,
  relatedCards = [],
}: Readonly<AddToCartOverviewProps>) {
  const urlCountry = getActBaseImagePath() || '/content/dam/msc-cruises/b2c-assets/act';
  const { bookingNumber = '' } = getActiveSession();
  const { data: booking } = useGetBookingQuery(bookingNumber || skipToken);
  const confirmationDrawerProps = useSelector(
    (state: RootState) => state.myAreaCommonConfig.confirmationDrawerProps
  );
  const { shopExcursion, shopOBS } = useSelector((state: RootState) => state.myCruise);
  const cartItems = useSelector((state: RootState) => state.myAreaCart.items);
  const isLayerExcursionOpen = useSelector(
    (state: RootState) => state.myAreaDrawers.isLayerMyAreaExcursionOpen
  );
  const hasWindow = globalThis.window !== undefined;
  const [isMobile, setIsMobile] = useState(
    hasWindow ? globalThis.window.innerWidth <= breakpoints.md : false
  );

  const passengersPricingText = confirmationDrawerProps?.passengersPricingText;

  // A combined card merges the adult package with its linked package(s) (e.g. minor ALLINC).
  // All title/passenger/price logic below operates on the full set of codes, not a single one.
  const mergedCodes = useMemo(
    () =>
      [postBookingCard, ...relatedCards]
        .map((card) => card?.code)
        .filter((code): code is string => Boolean(code)),
    [postBookingCard, relatedCards]
  );
  const mergedCodeSet = useMemo(() => new Set(mergedCodes), [mergedCodes]);
  const mergedTitle = [postBookingCard, ...relatedCards]
    .map((card) => card?.description)
    .filter((description): description is string => Boolean(description))
    .join(', ');

  const cartItemsForCards = useMemo(
    () => cartItems.filter((item) => item.code && mergedCodeSet.has(item.code)),
    [cartItems, mergedCodeSet]
  );
  const passengersId = cartItemsForCards.length
    ? Array.from(
        new Set(
          cartItemsForCards.flatMap(
            (item) =>
              item.passengers
                ?.map((passenger) => passenger.id)
                .filter((id): id is string => Boolean(id)) ?? []
          )
        )
      )
    : Object.values(passengersToAdd ?? []).flatMap((inner) =>
        Object.entries(inner)
          .filter(([, value]) => value)
          .map(([id]) => id)
      );
  const selectedPassengerIdSet = useMemo(() => new Set(passengersId), [passengersId]);
  const activeCruiseId = getActiveSession().cruiseId;
  const passengerData = useMemo(() => booking?.passengers ?? [], [booking]);
  const loggedUserCabinId = useMemo(
    () => getCabinIdOfTheLoggedUser(booking ?? undefined),
    [booking]
  );
  const cabinsForPassengerList = useMemo(
    () =>
      getOrderedCabinsForPassengerDrawer(booking, activeCruiseId || undefined, loggedUserCabinId),
    [booking, activeCruiseId, loggedUserCabinId]
  );
  const passengersPerCabin = useMemo(() => {
    const record: Record<string, PassengerI[]> = {};
    cabinsForPassengerList.forEach((cabin) => {
      const ids = new Set(
        (cabin.passengers ?? []).map((pax) =>
          typeof pax === 'object' && pax !== null ? pax.id : pax
        )
      );
      if (!cabin.id) {
        return;
      }
      record[cabin.id] = passengerData.filter((passenger) => passenger.id && ids.has(passenger.id));
    });
    return record;
  }, [cabinsForPassengerList, passengerData]);
  const placeholderImagePath = isLayerExcursionOpen
    ? getActPlaceholderImagePath()
    : getObsPlaceholderImagePath();
  const image = isLayerExcursionOpen
    ? `${urlCountry}/${postBookingCard?.code}.jpg`
    : `${getObsBaseImagePath()}/${postBookingCard?.code}.jpg`;
  const localizedAssetAlt = useAssetAltText(
    postBookingCard?.code,
    isLayerExcursionOpen ? 'act' : 'obs'
  );

  const bookedPassengerTotalsById = useMemo(() => {
    const result = new Map<string, BookedPassengerRefundTotals>();
    if (!useBookedRefundPricing || !booking || !mergedCodes.length) {
      return result;
    }

    const prepaidBookingsForCruise = activeCruiseId
      ? (booking.prepaid?.bookings ?? []).filter(
          (prepaidBooking) => prepaidBooking.cruise.id === activeCruiseId
        )
      : booking.prepaid?.bookings ?? [];

    if (isLayerExcursionOpen) {
      const bookedExcursions = prepaidBookingsForCruise.flatMap(
        (prepaidBooking) => prepaidBooking.excursions ?? []
      ) as BookedPrepaidRefundLine[];

      addPerPassengerBookedRefundLines(
        result,
        bookedExcursions,
        mergedCodeSet,
        selectedPassengerIdSet
      );
      addPerCabinBookedRefundLines(
        result,
        bookedExcursions,
        mergedCodeSet,
        selectedPassengerIdSet,
        passengersPerCabin
      );
      return result;
    }

    const bookedOnboardServices = prepaidBookingsForCruise.flatMap(
      (prepaidBooking) => prepaidBooking.onboardServices ?? []
    ) as BookedPrepaidRefundLine[];

    addPerPassengerBookedRefundLines(
      result,
      bookedOnboardServices,
      mergedCodeSet,
      selectedPassengerIdSet
    );
    addPerCabinBookedRefundLines(
      result,
      bookedOnboardServices,
      mergedCodeSet,
      selectedPassengerIdSet,
      passengersPerCabin
    );

    return result;
  }, [
    useBookedRefundPricing,
    booking,
    mergedCodeSet,
    mergedCodes.length,
    activeCruiseId,
    selectedPassengerIdSet,
    isLayerExcursionOpen,
    passengersPerCabin,
  ]);

  const passengersIdForOverview = useBookedRefundPricing
    ? Array.from(bookedPassengerTotalsById.keys())
    : passengersId;
  const passengersName = booking
    ? getNamesOfAlreadyBookedPassengers(passengersIdForOverview, booking)
    : [];

  const obsCodeForPricing = shopOBS?.code;

  const passengerPrices = useMemo(() => {
    // Price source is Redux shop state only; no network side-effects in add-to-cart flow.
    if (isLayerExcursionOpen) {
      return postBookingCard?.code === shopExcursion?.code
        ? shopExcursion?.prices?.perPassenger ?? []
        : [];
    }

    return shopOBS?.prices?.perPassenger ?? [];
  }, [isLayerExcursionOpen, shopExcursion, shopOBS]);

  const priceBreakdown = useMemo(() => {
    if (!passengersIdForOverview.length || !passengersPricingText) {
      return [] as PriceBreakdownEntry[];
    }

    const priceMap = new Map<string, AddToCartOverviewPriceMapRow>();

    passengersIdForOverview.forEach((passengerId) => {
      if (useBookedRefundPricing) {
        mergeBookedRefundPassengerIntoPriceMap(priceMap, passengerId, bookedPassengerTotalsById);
        return;
      }

      const passengerPrice = findShopPassengerPriceRow(
        passengerPrices as ShopDrawerPassengerPrice[],
        passengerId,
        isLayerExcursionOpen,
        mergedCodeSet,
        obsCodeForPricing
      );
      mergeShopPassengerIntoPriceMap(priceMap, passengerPrice);
    });

    return Array.from(priceMap.values()).map(
      ({ count, discounted, original }): PriceBreakdownEntry => {
        const text = managePlurals(
          count,
          replacePlaceholders(passengersPricingText, {
            numberOfPassengers: count.toString(),
            itemPrices: discounted,
            itemPrice: discounted,
            price: discounted,
            prices: discounted,
          })
        );

        const hasPriceInResult = text.includes(discounted);

        return {
          text,
          originalPrice: hasPriceInResult ? original : undefined,
        };
      }
    );
  }, [
    isLayerExcursionOpen,
    passengersIdForOverview,
    passengerPrices,
    passengersPricingText,
    mergedCodeSet,
    obsCodeForPricing,
    useBookedRefundPricing,
    bookedPassengerTotalsById,
  ]);

  const totalPrice = useMemo<TotalPrice>(() => {
    if (!mergedCodes.length) {
      return { final: '', strikeThrough: undefined };
    }

    // Sum the per-item cart totals across the combined codes (adult + linked package(s)).
    let summedAfter = 0;
    let summedStrike = 0;
    let hasItemPrice = false;
    cartItemsForCards.forEach((item) => {
      if (!item.code) {
        return;
      }
      const itemPrice = getPriceByTypeAndCode(
        (item.itemType as ItemTypeEnum | null) ?? null,
        item.code
      );
      if (!itemPrice?.price) {
        return;
      }
      hasItemPrice = true;
      const priceValue = Number.parseFloat(itemPrice.price);
      summedAfter += Number.isNaN(priceValue) ? 0 : priceValue;
      const strike = itemPrice.strikeThroughPrice
        ? Number.parseFloat(itemPrice.strikeThroughPrice)
        : priceValue;
      summedStrike += Number.isNaN(strike) ? 0 : strike;
    });

    if (hasItemPrice) {
      const final = getFormatPriceCurrencyOrder(formatPrice(roundPrice(summedAfter)));
      const strikeThrough =
        summedStrike > summedAfter
          ? getFormatPriceCurrencyOrder(formatPrice(roundPrice(summedStrike)))
          : '';

      return {
        final,
        strikeThrough: strikeThrough || undefined,
      };
    }

    if (!passengersIdForOverview.length) {
      return { final: '', strikeThrough: undefined };
    }

    const accum = { totalAfterDiscount: 0, totalBeforePromo: 0 };
    passengersIdForOverview.forEach((passengerId) => {
      if (useBookedRefundPricing) {
        accumulateTotalsFromBookedRefundPassenger(passengerId, bookedPassengerTotalsById, accum);
        return;
      }

      const passengerPrice = findShopPassengerPriceRow(
        passengerPrices as ShopDrawerPassengerPrice[],
        passengerId,
        isLayerExcursionOpen,
        mergedCodeSet,
        obsCodeForPricing
      );
      accumulateTotalsFromShopPassenger(passengerPrice, accum);
    });
    const { totalAfterDiscount, totalBeforePromo } = accum;

    if (!totalAfterDiscount) {
      return { final: '', strikeThrough: undefined };
    }

    const final = getFormatPriceCurrencyOrder(formatPrice(roundPrice(totalAfterDiscount)));
    const beforeFormatted =
      totalBeforePromo > totalAfterDiscount
        ? getFormatPriceCurrencyOrder(formatPrice(roundPrice(totalBeforePromo)))
        : '';

    return {
      final,
      strikeThrough: beforeFormatted || undefined,
    };
  }, [
    cartItemsForCards,
    mergedCodes.length,
    isLayerExcursionOpen,
    passengerPrices,
    passengersIdForOverview,
    mergedCodeSet,
    obsCodeForPricing,
    useBookedRefundPricing,
    bookedPassengerTotalsById,
  ]);

  const isPriceLoading = false;

  const shouldShowAllCabinLabel = useMemo(() => {
    if (!postBookingCard || !booking) {
      return false;
    }

    let isPerCabinPricing = false;
    if (isLayerExcursionOpen) {
      if (useBookedRefundPricing) {
        isPerCabinPricing = postBookingCard.priceBasis === PriceBasisEnum.PER_CABIN;
      } else {
        isPerCabinPricing = shopExcursion?.priceBasis === PriceBasisEnum.PER_CABIN;
      }
    } else {
      isPerCabinPricing = postBookingCard.priceBasis === PriceBasisEnum.PER_CABIN;
    }

    return isPerCabinPricing;
  }, [
    booking,
    postBookingCard,
    isLayerExcursionOpen,
    useBookedRefundPricing,
    shopExcursion?.priceBasis,
  ]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= breakpoints.md);

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const passengersContent = getPassengersContent({
    passengersName,
    isMobile,
    shouldShowAllCabinLabel,
    allCabinLabel: confirmationDrawerProps?.allCabinLabel,
  });

  const priceBreakdownContent = getPriceBreakdownContent({
    isPriceLoading,
    shouldShowAllCabinLabel,
    priceBreakdown,
    isMobile,
  });

  return (
    <div className="add-to-cart-overview">
      <Image
        src={image}
        alt={localizedAssetAlt || mergedTitle}
        styleOption="add-to-cart-overview__image cmp-image__overlay"
        placeholderImagePath={placeholderImagePath}
      />
      <div className="add-to-cart-overview__body">
        <Text
          text={mergedTitle}
          styleOption={'h5 add-to-cart-overview__body__title'}
          tagHtml={'h5'}
        />
        {/* All-cabin label is rendered in the passengers section only */}
        <div className="add-to-cart-overview__body__passengers">{passengersContent}</div>
        <div className="add-to-cart-overview__body__price-breakdown">{priceBreakdownContent}</div>
        {isPriceLoading ? (
          <SkeletonLoader
            width="90px"
            height="22px"
            styleOption="cmp-skeleton__loader"
          />
        ) : (
          totalPrice.final && (
            <div className="add-to-cart-overview__body__total-price-wrapper">
              <Text
                text={totalPrice.final}
                styleOption="add-to-cart-overview__body__total-price cmp-text__big bold"
              />
              {totalPrice.strikeThrough && (
                <Text
                  text={totalPrice.strikeThrough}
                  styleOption="add-to-cart-overview__body__total-price-strikethrough"
                />
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

export default AddToCartOverview;
